import React, { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import Button from '../ui/Button';
import Drawer from '../ui/Drawer';
import ConfirmDialog from '../ui/ConfirmDialog';
import { apiFetch } from '../../services/apiClient';
import { INSTITUTIONS } from '../../services/staffFields';
import { GRADE_LABELS } from '../../services/onCallRoster';
import AddNewButton from '../ui/AddNewButton';
import ItemCard, { CARD_GRID, CardSection, Chip } from '../ui/ItemCard';

// =============================================================================
// On-call roles — the overnight duties this department rosters each night.
// =============================================================================
// Each role is filled from one on-call grade (the person's on-call rank), may
// be at a site, and may be limited to that site's staff. The solver, the
// console's columns and its checks all read this list (GET/PUT
// /api/on-call/roles). Each role keeps a permanent key that duties and the
// constraints file refer to, so renaming one moves nothing; a new role gets its
// key from its name when first saved.
//
// Lives on Ranks & Positions (Oct 2026; it was in System Settings), beside the
// on-call grade names: a role is defined by the grade that fills it, so the
// pools and the duties they cover are read together, in the department's own
// grade names (`gradeLabels`). One card per role, edited in a side panel,
// added from the dashed button at the bottom, like every list (Oct 2026). Each
// save sends the whole list, as the API takes it.
// =============================================================================

const label = 'text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1';
const box = 'w-full h-[44px] px-4 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]';

const gradeName = (g, gradeLabels) => `${g} · ${(gradeLabels[g] || '').trim() || GRADE_LABELS[g]}`;

/** Add or edit one role. `role` null = a new one. */
function RoleDrawer({ role, onClose, onSave, saving, error, gradeLabels }) {
  const [form, setForm] = useState(role
    ? { ...role, site: role.site || '' }
    : { key: null, name: '', grade: 2, site: '', site_staff_only: false });
  const set = (patch) => setForm(f => {
    const next = { ...f, ...patch };
    if (!next.site) next.site_staff_only = false;        // "only that site's staff" needs a site
    return next;
  });
  return (
    <Drawer isOpen onClose={onClose} title={role ? 'Edit On-call Role' : 'New On-call Role'}
      footer={(
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} disabled={saving} className="flex-1">Cancel</Button>
          <Button onClick={() => onSave(form)} isLoading={saving} disabled={!form.name.trim()} className="flex-1">
            {role ? 'Save' : 'Add role'}
          </Button>
        </div>
      )}>
      <div className="space-y-6">
        <div className="flex flex-col gap-1.5">
          <label className={label} htmlFor="role-name">Role</label>
          <input id="role-name" aria-label="Role name" autoFocus value={form.name}
            onChange={e => set({ name: e.target.value })} placeholder="e.g. MO (NUH)" className={box} />
          {role && <p className="text-[11px] text-slate-400 ml-1">Renaming moves nothing: past duties keep this role.</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className={label} htmlFor="role-grade">Filled by</label>
          <select id="role-grade" aria-label="Grade" value={form.grade}
            onChange={e => set({ grade: Number(e.target.value) })} className={`${box} cursor-pointer`}>
            {Object.keys(GRADE_LABELS).map(g => <option key={g} value={g}>{gradeName(g, gradeLabels)}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className={label} htmlFor="role-site">Site</label>
          <select id="role-site" aria-label="Site" value={form.site}
            onChange={e => set({ site: e.target.value })} className={`${box} cursor-pointer`}>
            <option value="">No site</option>
            {INSTITUTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <p className="text-[11px] text-slate-400 ml-1">Post-call rules and the home-site preference use it.</p>
        </div>
        <label className={`flex items-center gap-2 text-sm font-bold ${form.site ? 'text-slate-700' : 'text-slate-300'}`}>
          <input type="checkbox" aria-label="Own staff only" disabled={!form.site} checked={!!form.site_staff_only}
            onChange={e => set({ site_staff_only: e.target.checked })} />
          {form.site ? `${form.site} staff only` : 'Own staff only (needs a site)'}
        </label>
        <p className="text-xs text-slate-500 leading-relaxed">
          Somebody with no institution recorded may take any role of their grade.
        </p>
        {error && <p className="text-xs font-bold text-rose-600">{error}</p>}
      </div>
    </Drawer>
  );
}

export default function OnCallRolesCard({ onSaved, gradeLabels = {} }) {
  const [roles, setRoles] = useState(null);
  const [editing, setEditing] = useState(null);     // null | { index: number | null }
  const [removing, setRemoving] = useState(null);   // an index
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiFetch('/api/on-call/roles')
      .then(r => r.json())
      .then(d => { if (d?.roles) setRoles(d.roles); })
      .catch(() => setError('Could not load the on-call roles'));
  }, []);

  if (!roles) return null;

  /** Send the whole list; true when the server took it. */
  const send = async (next) => {
    setSaving(true); setError(null);
    try {
      const res = await apiFetch('/api/on-call/roles', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roles: next.map(r => ({ ...r, name: r.name.trim(), site: r.site || null })),
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.detail || 'Save failed'); return false; }
      setRoles(d.roles);
      if (onSaved) onSaved();
      return true;
    } catch { setError('Save failed'); return false; }
    finally { setSaving(false); }
  };

  const saveRole = async (form) => {
    const i = editing.index;
    const next = i === null ? [...roles, form] : roles.map((r, j) => (j === i ? form : r));
    if (await send(next)) setEditing(null);
  };

  return (
    <div className="space-y-4">
      <div className="ml-1">
        <h3 className="text-sm font-black text-slate-900 tracking-tight">On-call Roles</h3>
        <p className="text-xs text-slate-500 mt-1 font-medium leading-relaxed max-w-2xl">
          The duties covered every night. Each is filled from one on-call grade, may be at a site, and
          may be limited to that site&apos;s own staff.
        </p>
      </div>

      <div className={CARD_GRID}>
        {roles.map((r, i) => (
          <ItemCard key={r.key || `new-${i}`} title={r.name}
            onEdit={() => { setError(null); setEditing({ index: i }); }}
            onDelete={() => setRemoving(i)}
            deleteDisabledReason={roles.length === 1 ? 'A department needs at least one role' : undefined}
            footerLeft={`Filled by ${gradeName(r.grade, gradeLabels)}`}>
            <CardSection icon={MapPin} label="Site">
              <div className="flex flex-wrap gap-1.5">
                {r.site ? <Chip tone="teal">{r.site}</Chip> : <Chip>No site</Chip>}
                {r.site_staff_only && <Chip tone="amber">{`${r.site} staff only`}</Chip>}
              </div>
            </CardSection>
          </ItemCard>
        ))}
      </div>

      <AddNewButton label="New role" onClick={() => { setError(null); setEditing({ index: null }); }} />
      {error && !editing && <p className="text-xs font-bold text-rose-600 ml-1">{error}</p>}

      {editing && (
        <RoleDrawer role={editing.index === null ? null : roles[editing.index]} saving={saving} error={error}
          gradeLabels={gradeLabels} onClose={() => { setError(null); setEditing(null); }} onSave={saveRole} />
      )}
      <ConfirmDialog isOpen={removing !== null} onClose={() => setRemoving(null)} tone="danger"
        title={`Remove ${removing !== null ? roles[removing]?.name : 'this role'}?`}
        body="Its past duties stay on record. From now on it is no longer rostered."
        confirmLabel="Remove" isLoading={saving}
        onConfirm={async () => { if (await send(roles.filter((_, j) => j !== removing))) setRemoving(null); }} />
    </div>
  );
}
